import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Link as LinkIcon, Users, CheckCircle2, Copy, X, Search, Key, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { error as logError } from '../../../utils/logger';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { BulkImport } from './BulkImport';
import { getLinkedLearners, findAccountByLinkCode, isLearnerAccount } from '../../../accounts';
import { type TeacherAccount, type Classroom, type LearnerAccount } from '../../../accounts';
interface AddStudentPanelV2Props {
  activeAccount: TeacherAccount;
  onComplete: () => void;
  onCancel: () => void;
  onClose?: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  inline?: boolean;
}
export function AddStudentPanelV2({
  activeAccount,
  onComplete,
  onCancel,
  onClose,
  classrooms = [],
  selectedClassroomId = null,
  inline = false
}: AddStudentPanelV2Props) {
  const {
    accounts,
    createAndLinkStudent,
    linkTeacherToLearnerById,
    addLearnerToClassroom
  } = useAppStore();
  const [activeTab, setActiveTab] = useState<'new' | 'link' | 'bulk'>('new');
  // Classroom context
  const resolvedClassroomId = selectedClassroomId || (classrooms.length === 1 ? classrooms[0].id : null);
  const [panelClassroomId, setPanelClassroomId] = useState<string>(resolvedClassroomId || '');
  const effectiveClassroomId = resolvedClassroomId || panelClassroomId || null;
  const activeClassroom = effectiveClassroomId ? classrooms.find(c => c.id === effectiveClassroomId) : null;
  // Form states
  const [newStudentName, setNewStudentName] = useState('');
  const [fallbackGrade, setFallbackGrade] = useState(activeClassroom ? activeClassroom.groupLevel.toString() : '6');
  const [searchQuery, setSearchQuery] = useState('');
  const [linkCodeInput, setLinkCodeInput] = useState('');
  const [linkMode, setLinkMode] = useState<'search' | 'code'>('search');
  // Result states
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [copied, setCopied] = useState(false);
  // Learners logic for linking
  const linkedLearners = getLinkedLearners(accounts, activeAccount.id);
  const availableLearners = useMemo(() => {
    const linkedIds = new Set(linkedLearners.map(l => l.id));
    const all: LearnerAccount[] = [];
    for (const acc of Object.values(accounts.accountsById)) {
      if (isLearnerAccount(acc) && !linkedIds.has(acc.id)) {
        all.push(acc);
      }
    }
    return all.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [accounts.accountsById, linkedLearners]);
  const filteredLearners = useMemo(() => {
    if (!searchQuery.trim()) return availableLearners;
    const q = searchQuery.toLowerCase();
    return availableLearners.filter(l => l.displayName.toLowerCase().includes(q) || l.username && l.username.toLowerCase().includes(q));
  }, [availableLearners, searchQuery]);
  // Handlers
  const handleCreateStudent = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    setGeneratedCode('');
    if (!newStudentName.trim()) {
      setFormError('Vul een naam in.');
      return;
    }
    const grade = activeClassroom ? activeClassroom.groupLevel : parseInt(fallbackGrade, 10);
    try {
      const {
        studentId,
        linkCode
      } = createAndLinkStudent(activeAccount.id, {
        displayName: newStudentName.trim(),
        grade
      }, effectiveClassroomId || undefined);
      setGeneratedCode(linkCode);
      setSuccessMessage(`${newStudentName.trim()} is toegevoegd!`);
      setNewStudentName(''); // Reset for next entry
    } catch (err) {
      logError('Failed to create student', err);
      setFormError('Er is een fout opgetreden bij het aanmaken.');
    }
  };
  const handleLinkLearner = (learner: LearnerAccount) => {
    setFormError('');
    setSuccessMessage('');
    setGeneratedCode('');
    if (effectiveClassroomId && activeClassroom) {
      if (activeClassroom.learnerIds.includes(learner.id)) {
        setFormError(`${learner.displayName} zit al in deze klas.`);
        return;
      }
    }
    try {
      if (!linkedIds.has(learner.id)) {
        linkTeacherToLearnerById(activeAccount.id, learner.id);
      }
      if (effectiveClassroomId) {
        addLearnerToClassroom(activeAccount.id, effectiveClassroomId, learner.id);
      }
      setSuccessMessage(`${learner.displayName} is gekoppeld!`);
      setSearchQuery('');
      setLinkCodeInput('');
    } catch (err) {
      setFormError('Er is een fout opgetreden bij het koppelen.');
    }
  };
  const handleLookupCode = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    if (!linkCodeInput.trim()) {
      setFormError('Vul een koppelcode in.');
      return;
    }
    const found = findAccountByLinkCode(accounts, linkCodeInput.trim().toUpperCase());
    if (!found || !isLearnerAccount(found)) {
      setFormError('Geen leerling gevonden met deze koppelcode.');
      return;
    }
    handleLinkLearner(found);
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className={inline ? 'flex flex-col bg-slate-50/50 p-4' : 'bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden flex flex-col p-6'} data-id="element-5416">
      {/* Tabs */}
      <div className="flex items-center justify-between mb-4" data-id="element-5417">
        <div className="flex gap-2" data-id="element-5418">
          <button onClick={() => {
          setActiveTab('new');
          setFormError('');
          setSuccessMessage('');
        }} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeTab === 'new' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-100'}`} data-id="element-5419">
            <div className="flex items-center gap-2" data-id="element-5420">
              <UserPlus className="w-4 h-4" data-id="element-5421" />
              Nieuw
            </div>
          </button>
          <button onClick={() => {
          setActiveTab('link');
          setFormError('');
          setSuccessMessage('');
        }} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeTab === 'link' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-100'}`} data-id="element-5422">
            <div className="flex items-center gap-2" data-id="element-5423">
              <LinkIcon className="w-4 h-4" data-id="element-5424" />
              Koppelen
            </div>
          </button>
        </div>
        {!inline && onClose && <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-id="element-5425">
            <X className="w-5 h-5" data-id="element-5426" />
          </button>}
      </div>

      {/* Success Message Inline */}
      <AnimatePresence data-id="element-5427">
        {successMessage && <motion.div initial={{
        opacity: 0,
        y: -10,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        y: -10,
        height: 0
      }} className="mb-4" data-id="element-5428">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between" data-id="element-5429">
              <div className="flex items-center gap-3" data-id="element-5430">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-5431" />
                <div data-id="element-5432">
                  <p className="text-sm font-bold text-emerald-800" data-id="element-5433">
                    {successMessage}
                  </p>
                  {generatedCode && <div className="flex items-center gap-2 mt-1" data-id="element-5434">
                      <span className="text-xs text-emerald-600" data-id="element-5435">
                        Koppelcode:
                      </span>
                      <span className="font-mono font-bold text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded" data-id="element-5436">
                        {generatedCode}
                      </span>
                      <button onClick={copyToClipboard} className="text-emerald-600 hover:text-emerald-800 transition-colors" title="Kopieer code" data-id="element-5437">
                        {copied ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-5438" /> : <Copy className="w-3.5 h-3.5" data-id="element-5439" />}
                      </button>
                    </div>}
                </div>
              </div>
              <button onClick={() => setSuccessMessage('')} className="p-1 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg" data-id="element-5440">
                <X className="w-4 h-4" data-id="element-5441" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Error Message Inline */}
      {formError && <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-center gap-3" data-id="element-5442">
          <AlertTriangle className="w-5 h-5 text-rose-500" data-id="element-5443" />
          <p className="text-sm font-bold text-rose-800" data-id="element-5444">{formError}</p>
        </div>}

      {/* Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-id="element-5445">
        {activeTab === 'new' && <form onSubmit={handleCreateStudent} className="flex flex-col gap-3" data-id="element-5446">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-5447">
              <div className="flex-1 w-full" data-id="element-5448">
                <Input variant="admin" placeholder="Naam van de leerling" value={newStudentName} onChange={e => setNewStudentName(e.target.value)} autoFocus data-id="element-5449" />
              </div>

              {!resolvedClassroomId && classrooms.length > 1 && <div className="w-full sm:w-48" data-id="element-5450">
                  <Select variant="admin" value={panelClassroomId} onChange={e => setPanelClassroomId(e.target.value)} options={[{
              value: '',
              label: 'Geen klas'
            }, ...classrooms.map(c => ({
              value: c.id,
              label: `${c.name} (Gr ${c.groupLevel})`
            }))]} data-id="element-5451" />
                </div>}

              {!effectiveClassroomId && <div className="w-full sm:w-32" data-id="element-5452">
                  <Select variant="admin" value={fallbackGrade} onChange={e => setFallbackGrade(e.target.value)} options={[{
              value: '6',
              label: 'Groep 6'
            }, {
              value: '7',
              label: 'Groep 7'
            }, {
              value: '8',
              label: 'Groep 8'
            }]} data-id="element-5453" />
                </div>}

              <Button type="submit" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6" data-id="element-5454">
                Toevoegen
              </Button>
            </div>
          </form>}

        {activeTab === 'link' && <div className="flex flex-col gap-4" data-id="element-5455">
            <div className="flex gap-2 border-b border-slate-100 pb-2" data-id="element-5456">
              <button onClick={() => setLinkMode('search')} className={`text-sm font-bold px-3 py-1.5 rounded-md transition-colors ${linkMode === 'search' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50'}`} data-id="element-5457">
                Zoeken
              </button>
              <button onClick={() => setLinkMode('code')} className={`text-sm font-bold px-3 py-1.5 rounded-md transition-colors ${linkMode === 'code' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50'}`} data-id="element-5458">
                Koppelcode
              </button>
            </div>

            {linkMode === 'search' ? <div className="space-y-3" data-id="element-5459">
                <div className="relative" data-id="element-5460">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-5461" />
                  <Input variant="admin" placeholder="Zoek op naam..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" autoFocus data-id="element-5462" />
                </div>

                {searchQuery.trim() && <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-id="element-5463">
                    {filteredLearners.length === 0 ? <div className="p-4 text-center text-sm text-slate-500" data-id="element-5464">
                        Geen leerlingen gevonden.
                      </div> : filteredLearners.map(learner => <div key={learner.id} className="flex items-center justify-between p-3 hover:bg-slate-50" data-id="element-5465">
                          <div className="flex items-center gap-3" data-id="element-5466">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm" data-id="element-5467">
                              {learner.displayName.charAt(0).toUpperCase()}
                            </div>
                            <div data-id="element-5468">
                              <div className="font-bold text-sm text-slate-900" data-id="element-5469">
                                {learner.displayName}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-5470">
                                Groep {learner.grade}
                              </div>
                            </div>
                          </div>
                          <Button size="sm" variant="secondary" onClick={() => handleLinkLearner(learner)} data-id="element-5471">
                            Koppelen
                          </Button>
                        </div>)}
                  </div>}
              </div> : <form onSubmit={handleLookupCode} className="flex gap-3" data-id="element-5472">
                <div className="relative flex-1" data-id="element-5473">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-5474" />
                  <Input variant="admin" placeholder="Bijv. A1B2C3D4" value={linkCodeInput} onChange={e => setLinkCodeInput(e.target.value.toUpperCase())} className="pl-9 font-mono uppercase" maxLength={8} autoFocus data-id="element-5475" />
                </div>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-id="element-5476">
                  Koppelen
                </Button>
              </form>}
          </div>}

        {activeTab === 'bulk' && <BulkImport teacherId={activeAccount.id} onComplete={onComplete} onCancel={() => setActiveTab('new')} classrooms={classrooms} selectedClassroomId={effectiveClassroomId} data-id="element-5477" />}
      </div>

      {activeTab !== 'bulk' && <div className="mt-4 text-center" data-id="element-5478">
          <button onClick={() => {
        setActiveTab('bulk');
        setFormError('');
        setSuccessMessage('');
      }} className="text-xs font-bold text-slate-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-1 mx-auto" data-id="element-5479">
            <Users className="w-3.5 h-3.5" data-id="element-5480" />
            Meerdere tegelijk importeren?
          </button>
        </div>}
    </div>;
}